/**
 * The queue a reviewer gets when they open a document review from the grid.
 *
 * Opening one row used to hand back that manifest's documents and nothing else,
 * so working through a filtered queue meant returning to the grid between every
 * manifest. The grid is already the working set — filtered, sorted, and paged by
 * the reviewer — so the batch is what it shows, entered at the row they clicked.
 */

/** What the batch needs off a grid row; the grid's own row type supplies more. */
export interface ReviewBatchRow {
  tenantId: string;
  documentReviewState?: string | null;
  documentIds: readonly string[];
}

export interface ReviewBatch {
  /** The documents to work, in the grid's own order. */
  documentIds: string[];
  /** Which of them to open on: the first belonging to the clicked row. */
  startAt: string | undefined;
}

const NEEDS_REVIEW = "NEEDS_REVIEW";

/**
 * Only one tenant's rows.
 *
 * The review resolves per-tenant feature flags, and the queue carries a single
 * tenant for the whole batch — so a batch spanning tenants would render later
 * documents against the wrong org's flags and quietly show a different screen.
 * The grid is cross-tenant, so this has to be enforced here rather than assumed.
 */
function inBatch(row: ReviewBatchRow, tenantId: string): boolean {
  return (
    row.tenantId === tenantId &&
    row.documentReviewState === NEEDS_REVIEW &&
    row.documentIds.length > 0
  );
}

export function reviewBatchFromGrid({
  rows,
  clicked,
  limit,
}: {
  /** The rows the grid is currently showing, in display order. */
  rows: readonly ReviewBatchRow[];
  clicked: ReviewBatchRow;
  /** How many documents the queue will carry. */
  limit: number;
}): ReviewBatch {
  const clickedIds = [...clicked.documentIds];
  if (clickedIds.length === 0) return { documentIds: [], startAt: undefined };

  const candidates = rows.filter((row) => inBatch(row, clicked.tenantId));
  const ids = [...new Set(candidates.flatMap((row) => [...row.documentIds]))];

  // A row the grid is not showing — a stale render, or one whose state changed
  // under the reviewer. Its own documents are still a queue worth opening.
  const startIndex = ids.indexOf(clickedIds[0]);
  if (startIndex < 0) return { documentIds: clickedIds.slice(0, limit), startAt: clickedIds[0] };

  if (ids.length <= limit) return { documentIds: ids, startAt: clickedIds[0] };

  // Over the cap: keep everything from the clicked row forward, then fill
  // backwards with what came before it. Forward first because that is the
  // direction the reviewer is about to work, and dropping what is ahead of them
  // would end the queue early for no reason they could see.
  const forward = ids.slice(startIndex, startIndex + limit);
  const backfill = ids.slice(Math.max(0, startIndex - (limit - forward.length)), startIndex);
  return { documentIds: [...backfill, ...forward], startAt: clickedIds[0] };
}
